import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChat } from '../../hooks/useChat';
import { useLanguage } from '../../lib/language';

// Text shadows for readability
const textShadow = '0 2px 4px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)';
const textShadowLight = '0 1px 3px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)';

const ChatWidget: React.FC = () => {
  const {
    messages,
    isOpen,
    isLoading,
    error,
    toggleChat,
    sendMessage,
    clearChat,
  } = useChat();

  const { language } = useLanguage();
  const es = language === 'es';

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue, language);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        onClick={toggleChat}
        className="fixed bottom-4 right-4 z-[90] flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full transition-colors"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              className="material-symbols-outlined text-white text-xl sm:text-2xl"
            >
              close
            </motion.span>
          ) : (
            <motion.span
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              className="material-symbols-outlined text-white text-xl sm:text-2xl"
            >
              chat
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed bottom-20 sm:bottom-24 right-4 z-[90] w-[calc(100vw-2rem)] sm:w-96 max-w-[400px] rounded-xl overflow-hidden flex flex-col"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              maxHeight: 'calc(100vh - 8rem)',
              height: '500px',
            }}
          >
            {/* Header */}
            <div
              className="px-4 py-3 flex items-center justify-between flex-shrink-0"
              style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 flex items-center justify-center"
                  style={{ border: '1px solid rgba(255, 255, 255, 0.2)' }}
                >
                  <span className="text-white font-black text-sm" style={{ textShadow }}>
                    M
                  </span>
                </div>
                <div>
                  <h3 className="text-white text-sm font-semibold" style={{ textShadow }}>
                    Musashi Studio
                  </h3>
                  <p className="text-white/50 text-[10px]" style={{ textShadow: textShadowLight }}>
                    {es ? 'Asistente IA' : 'AI Assistant'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearChat}
                  className="p-1.5 text-white/50 hover:text-white transition-colors"
                  title="Clear chat"
                >
                  <span className="material-symbols-outlined text-lg">refresh</span>
                </button>
                <button
                  onClick={toggleChat}
                  className="p-1.5 text-white/50 hover:text-white transition-colors"
                  title="Close chat"
                >
                  <span className="material-symbols-outlined text-lg">close</span>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Welcome message */}
              {messages.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8"
                >
                  <div
                    className="w-12 h-12 mx-auto mb-4 flex items-center justify-center"
                    style={{ border: '1px solid rgba(255, 255, 255, 0.2)' }}
                  >
                    <span className="text-white font-black text-xl" style={{ textShadow }}>
                      M
                    </span>
                  </div>
                  <h4 className="text-white font-semibold mb-2" style={{ textShadow }}>
                    {es ? 'Bienvenido a Musashi Studio' : 'Welcome to Musashi Studio'}
                  </h4>
                  <p
                    className="text-white/60 text-sm max-w-[250px] mx-auto"
                    style={{ textShadow: textShadowLight }}
                  >
                    {es
                      ? 'Te ayudo con soluciones empresariales, desarrollo con IA y a agendar una llamada.'
                      : "I'm here to help with enterprise solutions, AI development, and scheduling consultations."}
                  </p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {(es ? ['Servicios', 'Precios', 'Agendar llamada'] : ['Services', 'Pricing', 'Schedule a call']).map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => sendMessage(suggestion, language)}
                        className="text-[11px] px-3 py-1.5 rounded-full transition-colors"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: 'rgba(255, 255, 255, 0.7)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Message list */}
              {messages.map((message, index) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl ${
                      message.role === 'user'
                        ? 'rounded-br-md'
                        : 'rounded-bl-md'
                    }`}
                    style={{
                      backgroundColor:
                        message.role === 'user'
                          ? 'rgba(255, 255, 255, 0.15)'
                          : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <p
                      className="text-sm text-white/90 whitespace-pre-wrap"
                      style={{ textShadow: textShadowLight }}
                    >
                      {message.content}
                    </p>
                  </div>
                </motion.div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div
                    className="px-4 py-3 rounded-2xl rounded-bl-md"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          className="w-2 h-2 rounded-full bg-white/40"
                          animate={{ y: [0, -5, 0] }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            delay: i * 0.15,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Error message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-2"
                >
                  <p className="text-red-400 text-sm">{error}</p>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              className="p-3 flex-shrink-0"
              style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={es ? 'Escribe un mensaje…' : 'Type a message…'}
                  disabled={isLoading}
                  className="flex-1 bg-transparent text-white text-sm placeholder-white/40 focus:outline-none disabled:opacity-50"
                  style={{ textShadow: textShadowLight }}
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="p-1.5 text-white/50 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined text-xl">send</span>
                </button>
              </div>
              <p className="text-white/30 text-[9px] text-center mt-2">
                Powered by AI • hello@musashi.studio
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
